import React, { useState } from 'react';
import { Trash2, AlertTriangle, Database, RefreshCw, Plus, Sparkles } from 'lucide-react';

const ResetDataSettings = () => {
  const [confirmText, setConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);
  const [insertingDemo, setInsertingDemo] = useState(false);

  const handleInsertDemoData = async () => {
    if (!window.confirm('Insérer des données démo complètes?\n\n- 7 scans (réseau interne, kartoo.com, AD 2012, AD 2025, AWS, DMZ)\n- 100+ hosts\n- 200+ vulnérabilités\n- 50+ sessions d\'exploitation\n- Sorties de 20+ outils')) {
      return;
    }

    setInsertingDemo(true);
    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';

      const response = await fetch(`${backendUrl}/api/v1/system/insert-demo-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        alert(`✅ Données démo insérées avec succès!\n\nInséré:\n- ${data.inserted.scans} scans\n- ${data.inserted.hosts} hosts\n- ${data.inserted.vulnerabilities} vulnérabilités\n- ${data.inserted.sessions} sessions\n\nRafraîchissez la page pour voir les nouvelles données.`);
        // Recharger la page
        setTimeout(() => window.location.reload(), 2000);
      } else {
        const error = await response.json();
        alert('❌ Erreur: ' + (error.detail || 'Impossible d\'insérer les données'));
      }
    } catch (error) {
      console.error('Insert demo error:', error);
      alert('❌ Erreur: ' + error.message);
    } finally {
      setInsertingDemo(false);
    }
  };

  const handleResetData = async () => {
    if (confirmText !== 'SUPPRIMER') {
      alert('Veuillez taper "SUPPRIMER" pour confirmer');
      return;
    }

    if (!window.confirm('DERNIÈRE CONFIRMATION: Cette action est IRRÉVERSIBLE. Continuer ?')) {
      return;
    }

    setResetting(true);
    try {
      // TODO: Appeler API pour reset data
      const backendUrl = process.env.REACT_APP_API_URL || '';

      const response = await fetch(`${backendUrl}/api/v1/system/reset-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        alert('✅ Toutes les données ont été supprimées avec succès');
        setConfirmText('');
        // Recharger la page pour voir les changements
        window.location.reload();
      } else {
        alert('❌ Erreur lors de la suppression des données');
      }
    } catch (error) {
      console.error('Reset error:', error);
      alert('❌ Erreur: ' + error.message);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Insert Demo Data Button */}
      <div className="bg-gradient-to-r from-brand-green/20 via-blue-500/20 to-brand-green/20 border-2 border-brand-green/50 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-brand-green/20 border-2 border-brand-green flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-brand-green" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-brand-green">Insérer des Données Démo</h3>
              <p className="text-gray-300">Remplir la base avec un jeu de données réaliste pour démonstration</p>
            </div>
          </div>
          <button
            onClick={handleInsertDemoData}
            disabled={insertingDemo}
            className="px-6 py-4 bg-gradient-to-r from-brand-green to-blue-500 text-brand-black font-bold rounded-lg hover:shadow-lg hover:shadow-brand-green/30 transition-all disabled:opacity-50 flex items-center gap-3"
          >
            {insertingDemo ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Insertion...
              </>
            ) : (
              <>
                <Plus className="w-5 h-5" />
                Insérer Données Démo
              </>
            )}
          </button>
        </div>

        {/* Demo Data Details */}
        <div className="mt-4 bg-brand-black/30 rounded-lg p-4 border border-brand-green/20">
          <h4 className="text-white font-semibold mb-3">📊 Contenu des Données Démo:</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div className="bg-brand-black-lighter rounded p-3 border border-gray-700">
              <p className="text-brand-green font-semibold">7 Scans</p>
              <p className="text-gray-400 text-xs">Différents scénarios</p>
            </div>
            <div className="bg-brand-black-lighter rounded p-3 border border-gray-700">
              <p className="text-brand-green font-semibold">100+ Hosts</p>
              <p className="text-gray-400 text-xs">IP variées + domaines</p>
            </div>
            <div className="bg-brand-black-lighter rounded p-3 border border-gray-700">
              <p className="text-brand-green font-semibold">200+ Vulnérabilités</p>
              <p className="text-gray-400 text-xs">CVE réelles</p>
            </div>
            <div className="bg-brand-black-lighter rounded p-3 border border-gray-700">
              <p className="text-brand-green font-semibold">50+ Sessions</p>
              <p className="text-gray-400 text-xs">Exploitations réussies</p>
            </div>
            <div className="bg-brand-black-lighter rounded p-3 border border-gray-700">
              <p className="text-brand-green font-semibold">20+ Outils</p>
              <p className="text-gray-400 text-xs">Sorties réelles</p>
            </div>
            <div className="bg-brand-black-lighter rounded p-3 border border-gray-700">
              <p className="text-brand-green font-semibold">Screenshots</p>
              <p className="text-gray-400 text-xs">Preuves visuelles</p>
            </div>
          </div>

          <div className="mt-4 space-y-2 text-xs text-gray-400">
            <p className="flex items-center gap-2"><span className="text-brand-green">✓</span> Réseau interne 192.168.1.0/24 (45 hosts)</p>
            <p className="flex items-center gap-2"><span className="text-brand-green">✓</span> Domaine kartoo.com (Log4Shell, SQL Injection)</p>
            <p className="flex items-center gap-2"><span className="text-red-400">✓</span> Active Directory 2012 (EternalBlue, ZeroLogon, BlueKeep)</p>
            <p className="flex items-center gap-2"><span className="text-green-400">✓</span> Active Directory 2025 (sécurisé, peu de risques)</p>
            <p className="flex items-center gap-2"><span className="text-blue-400">✓</span> Infrastructure AWS (IAM issues, S3 exposé)</p>
            <p className="flex items-center gap-2"><span className="text-yellow-400">✓</span> DMZ (services exposés, configurations faibles)</p>
          </div>
        </div>
      </div>

      {/* Danger Zone Header */}
      <div className="bg-gradient-to-r from-red-900/40 via-red-800/30 to-red-900/40 border-2 border-red-500 rounded-xl p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center animate-pulse">
            <AlertTriangle className="w-8 h-8 text-red-400" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-red-400">ZONE DANGEREUSE</h3>
            <p className="text-gray-300">Actions irréversibles - Utilisez avec extrême prudence</p>
          </div>
        </div>

        <div className="bg-red-950/50 border border-red-500/30 rounded-lg p-4">
          <p className="text-red-300 text-sm font-medium mb-2">⚠️ AVERTISSEMENT CRITIQUE</p>
          <p className="text-gray-300 text-sm">
            La réinitialisation des données supprimera <span className="text-red-400 font-bold">DÉFINITIVEMENT</span> et 
            <span className="text-red-400 font-bold"> SANS POSSIBILITÉ DE RÉCUPÉRATION</span> toutes les informations 
            suivantes de la base de données.
          </p>
        </div>
      </div>

      {/* What will be deleted */}
      <div className="bg-brand-black rounded-xl border-2 border-red-500/50 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Database className="w-6 h-6 text-red-400" />
          <h4 className="text-lg font-bold text-white">Données qui seront SUPPRIMÉES:</h4>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-red-950/30 rounded-lg p-4 border border-red-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              <h5 className="text-white font-semibold">Scans</h5>
            </div>
            <ul className="text-sm text-gray-400 space-y-1">
              <li>• Tous les scans effectués</li>
              <li>• Historique complet</li>
              <li>• Timeline et logs</li>
            </ul>
          </div>

          <div className="bg-red-950/30 rounded-lg p-4 border border-red-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              <h5 className="text-white font-semibold">Vulnérabilités</h5>
            </div>
            <ul className="text-sm text-gray-400 space-y-1">
              <li>• Toutes les failles détectées</li>
              <li>• CVE et exploits</li>
              <li>• Recommandations IA</li>
            </ul>
          </div>

          <div className="bg-red-950/30 rounded-lg p-4 border border-red-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              <h5 className="text-white font-semibold">Hosts & Services</h5>
            </div>
            <ul className="text-sm text-gray-400 space-y-1">
              <li>• Tous les hosts découverts</li>
              <li>• Services détectés</li>
              <li>• Topologie réseau</li>
            </ul>
          </div>

          <div className="bg-red-950/30 rounded-lg p-4 border border-red-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              <h5 className="text-white font-semibold">Sessions & Rapports</h5>
            </div>
            <ul className="text-sm text-gray-400 space-y-1">
              <li>• Sessions d'exploitation</li>
              <li>• Rapports générés</li>
              <li>• Exports PDF/Excel</li>
            </ul>
          </div>
        </div>

        <div className="mt-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3">
          <p className="text-yellow-400 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <span className="font-semibold">Les utilisateurs et configurations système NE SERONT PAS supprimés</span>
          </p>
        </div>
      </div>

      {/* Confirmation Form */}
      <div className="bg-brand-black rounded-xl border-2 border-red-500/50 p-6">
        <h4 className="text-lg font-bold text-white mb-4">Confirmation Requise</h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Tapez <span className="text-red-400 font-bold">SUPPRIMER</span> pour confirmer:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Tapez SUPPRIMER en majuscules"
              className="w-full bg-brand-black-lighter border-2 border-red-500/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500"
            />
          </div>

          <button
            onClick={handleResetData}
            disabled={confirmText !== 'SUPPRIMER' || resetting}
            className="w-full px-6 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white font-bold rounded-lg hover:from-red-700 hover:to-red-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 border-2 border-red-500"
          >
            {resetting ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Suppression en cours...
              </>
            ) : (
              <>
                <Trash2 className="w-5 h-5" />
                SUPPRIMER TOUTES LES DONNÉES
              </>
            )}
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="text-2xl">ℹ️</div>
          <div className="text-sm text-gray-300">
            <p className="font-medium text-gray-200 mb-2">Quand utiliser cette fonction ?</p>
            <ul className="space-y-1 text-gray-400 text-xs">
              <li>• <strong>Tests:</strong> Nettoyer après des tests de la plateforme</li>
              <li>• <strong>Formation:</strong> Repartir à zéro après une session de formation</li>
              <li>• <strong>Nouvelle mission:</strong> Supprimer données d'une ancienne mission</li>
              <li>• <strong>Maintenance:</strong> Libérer de l'espace disque</li>
            </ul>
            <p className="text-yellow-400 mt-3 font-medium">
              ⚠️ Créez un backup manuel avant de réinitialiser!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetDataSettings;
